import { createMemo, untrack, createRoot, createComputed, children, createContext, useContext } from 'solid-js';
import { createComponent } from 'solid-js/web';

function splitAccessor(accessor) {
  return new Proxy(accessor, {
    get(target, prop) {
      target = createMemo(target);
      if (prop === Symbol.iterator) return function* () {
        const value = untrack(target);
        let i = 0;
        // Array specific iterator
        if (Array.isArray(value)) for (; i < value.length; ++i) yield createMemo(() => target()[i]);
        // Generic iterator
        else for (const _ of untrack(target)) {
          yield createMemo(() => {
            let j = 0;
            for (const item of target()) {
              if (i === j) return item;
              ++j;
            }
          });
          ++i;
        }
      };
      const value = untrack(target);
      return prop in value ? createMemo(() => target()[prop]) : undefined;
    }
  });
}

function signalValuePromise(signal, predicate) {
  return new Promise(resolve => {
    const value = signal();
    if (predicate(value)) resolve(value);
    createRoot(dispose => createComputed(() => {
      const value = signal();
      if (predicate(value)) {
        resolve(value);
        dispose();
      }
    }));
  });
}

function eventHandler(onEvent) {
  if (!onEvent) return () => {};
  if (typeof onEvent === "function") return onEvent;
  return e => onEvent[0](onEvent[1], e);
}

function reactiveCssVars(vars) {
  return Object.fromEntries(Object.entries(vars).map(([k, v]) => [`--${k}`, v()]));
}

function reactiveProps(props) {
  return Object.fromEntries(Object.keys(props).map(key => [key, () => props[key]]));
}
reactiveProps.withChildren = props => {
  return Object.fromEntries(Object.keys(props).map(key => [key, key === "children" ? children(() => props.children) : () => props[key]]));
};
reactiveProps.withPropsFor = props => {
  return Object.fromEntries(Object.keys(props).map(key => [key, key === "propsFor" ? PropsFor.createHandler(() => props.propsFor) : () => props[key]]));
};
function spread(props) {
  return Object.fromEntries(Object.keys(props).map(key => [key, props[key]()]));
}

let PropsFor;
(function (_PropsFor) {
  function createHandler(propsFor) {
    const _propsFor = createMemo(() => propsFor?.());
    const getPropsFor = (target, ...[context]) => {
      const props = _propsFor()?.[target];
      return (typeof props === "function" ? props(context) : props) || {};
    };
    return getPropsFor;
  }
  _PropsFor.createHandler = createHandler;
  function merge(...propsFors) {
    const propsLists = {};
    for (const propsFor of propsFors) {
      if (!propsFor) continue;
      for (const [target, props] of Object.entries(propsFor)) {
        // TODO: Add compatibility compile step for ??=
        // const targetData = propsLists[target] ??= {
        //   propsList: [],
        //   requiresContext: false,
        // }
        if (!propsLists[target]) propsLists[target] = {
          propsList: [],
          requiresContext: false
        };
        const targetData = propsLists[target];
        targetData.propsList.push(props);
        if (typeof props === "function") targetData.requiresContext = true;
      }
    }
    return Object.fromEntries(Object.entries(propsLists).map(([target, {
      propsList,
      requiresContext
    }]) => {
      const getProps = ctx => propsList.reduce((acc, curr) => ({
        ...acc,
        ...mergeFnProps(acc, typeof curr === "function" ? curr(ctx) : curr)
      }), {});
      return [target, requiresContext ? getProps : getProps()];
    }));
  }
  _PropsFor.merge = merge;
  function mergeFnProps(o1, o2) {
    return Object.fromEntries(Object.entries(o2).map(([key, value]) => [key, typeof value === "function" && typeof o1[key] === "function" ? (...args) => (o1[key](...args), value(...args)) : value]));
  }
})(PropsFor || (PropsFor = {}));
/**
 * @deprecated Use PropsFor.Handler instead
 */
/**
 * @deprecated Use PropsFor.createHandler instead
 */
const createPropsForHandler = PropsFor.createHandler;

/**
 * @deprecated Use PropsFor.merge instead
 */
const mergePropsFor = PropsFor.merge;

function createPropsProvider(key, mergers) {
  if (createPropsProvider.cache[key]) return createPropsProvider.cache[key];
  const mergerEntries = Object.entries({
    ...mergers,
    ...createPropsProvider.defaultMergers
  });
  const Context = createContext({});
  function PropsProvider(_props) {
    const {
      children,
      ...props
    } = reactiveProps(_props);
    const context = useContext(Context);
    return createComponent(Context.Provider, {
      get value() {
        return PropsProvider.merge(context, props);
      },
      get children() {
        return children();
      }
    });
  }
  PropsProvider.merge = (context, props) => {
    const merged = {
      ...context,
      ...props
    };
    for (const [prop, merge] of mergerEntries) if (prop in context && prop in props) merged[prop] = createMemo(() => merge(context, props));
    return merged;
  };
  PropsProvider.useContext = () => useContext(Context);
  PropsProvider.useMerge = props => PropsProvider.merge(PropsProvider.useContext(), props);
  createPropsProvider.cache[key] = PropsProvider;
  return PropsProvider;
}
createPropsProvider.defaultMergers = {
  class: (context, props) => [context.class?.(), props.class?.()].filter(Boolean).join(" "),
  propsFor: (context, props) => PropsFor.merge(context.propsFor, props.propsFor)
};

// Maintain references in dev refresh
createPropsProvider.cache = {};

export { PropsFor, createPropsForHandler, createPropsProvider, eventHandler, mergePropsFor, reactiveCssVars, reactiveProps, signalValuePromise, splitAccessor, spread };
//# sourceMappingURL=index.js.map
